import type { LoginInput, LoginResponse } from '@apontamentos/shared';
import type { GanttTask, GanttDependency, GanttResponse, TaskDependency } from '@apontamentos/shared';
import type {
  Project,
  Timesheet,
  TimesheetApproval,
  TimeEntry,
  Task,
  RaidItem,
  WbsNode,
  Activity,
  Comment,
  Objective,
  KeyResult,
  Scorecard,
  Blueprint,
  RequestForm,
  CustomFieldDefinition,
} from '@apontamentos/shared';
export type {
  Project,
  Timesheet,
  TimesheetApproval,
  TimeEntry,
  Task,
  RaidItem,
  WbsNode,
  Activity,
  Comment,
  Objective,
  KeyResult,
  Scorecard,
  Blueprint,
  RequestForm,
  CustomFieldDefinition,
  GanttTask,
  GanttDependency,
  TaskDependency,
};

// Tipos de resposta enriquecida da API (campos extras de JOIN não presentes nos schemas canônicos do shared)
// Shape real do backend (attachmentSchema em @apontamentos/shared):
// filename é o nome do arquivo, storageKey guarda a URL no MVP (sem upload real).
export type Attachment = {
  id: string;
  taskId: string;
  uploaderId: string;
  uploaderNome: string | null;
  filename: string;
  mime: string | null;
  sizeBytes: number;
  storageKey: string;
  criadoEm: string;
};

export type NavTreeNode = {
  id: string;
  tipo: 'portfolio' | 'program' | 'project';
  nome: string;
  parentId: string | null;
};

export type AclGrant = {
  id: string;
  principalType: 'user' | 'role';
  principalId: string;
  resourceType: 'portfolio' | 'program' | 'project' | 'blueprint' | 'request_form';
  resourceId: string;
  permission: 'view' | 'edit' | 'admin';
  grantedBy: string | null;
  grantedEm: string;
};

export type ApprovalRequest = {
  id: string;
  workflowId: string;
  workflowNome: string;
  entidade: string;
  entidadeId: string;
  status: 'pendente' | 'aprovado' | 'rejeitado';
  payload: Record<string, unknown>;
  iniciadoPor: string | null;
  iniciadoPorNome: string | null;
  iniciadoEm: string;
  finalizadoEm: string | null;
};

export type RequestSubmission = {
  id: string;
  formId: string;
  formNome: string;
  payload: Record<string, unknown>;
  submetidoPor: string | null;
  submetidoPorNome: string | null;
  submetidoEm: string;
  status: 'nova' | 'em_triagem' | 'aprovada' | 'rejeitada' | 'convertida';
  priorityScore: number | null;
  projetoId: string | null;
};

export type ApprovalWorkflow = {
  id: string;
  nome: string;
  escopo: string;
  passos: { nome: string; aprovadorRole: string; ordem: number }[];
  ativo: boolean;
  criadoEm: string;
};

export type PlannedVsActualRow = {
  projectId: string;
  projectCodigo: string;
  projectNome: string;
  taskId: string;
  taskNome: string;
  wbsCodigo: string;
  wbsNome: string;
  activityNome: string;
  estimativaHoras: number;
  horasRealizadas: number;
  progressoPct: number;
  status: string;
};

export type WeeklyReportRow = {
  semana: string;
  userId: string;
  userName: string;
  totalHoras: number;
  timesheetStatus: string;
};

export type Notification = {
  id: string;
  tipo: string;
  titulo: string;
  body: string | null;
  link: string | null;
  lidaEm: string | null;
  criadoEm: string;
};

export type SearchResult = {
  type: 'task' | 'project' | 'comment';
  id: string;
  title: string;
  body: string | null;
  projectId: string | null;
  projectNome: string | null;
  criadoEm: string;
};

export type AuditEntry = {
  id: string;
  userId: string;
  userNome: string;
  acao: string;
  entidade: string;
  entidadeId: string;
  diff: Record<string, unknown> | null;
  criadoEm: string;
};

// Campos batem com ResourceService.getUtilization (agregado por semana, não
// por lançamento) — ver apps/api/src/services/resource.service.pmo.ts.
export type ResourceUtilization = {
  userId: string;
  userNome: string;
  userEmail: string;
  semana: string;
  alocadoHoras: number;
  realizadoHoras: number;
  capacidadeHoras: number;
  utilizacaoPct: number;
};

export type SmtpConfig = {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password: string;
  fromName: string;
  fromAddress: string;
  emailNotificationsEnabled: boolean;
};

export type KanbanCard = {
  id: string;
  nome: string;
  status: string;
  prioridade: string;
  estimativaHoras: number;
  progressoPct: number;
  activityNome: string;
  wbsNome: string;
  wbsCodigo: string;
  projectId: string;
  projectNome: string;
  projectCodigo: string;
  dataFim: string | null;
  assigneeNomes: string[];
  assigneeIds: string[];
  isCritical: boolean;
};

export type CalendarTask = {
  id: string;
  nome: string;
  status: string;
  prioridade: string;
  dataInicio: string;
  dataFim: string;
  projectId: string;
  projectNome: string;
  projectCodigo: string;
  wbsCodigo: string;
};

export type CalendarEntry = {
  data: string;
  taskId: string;
  taskNome: string;
  horas: number;
  projectNome: string;
};

// Campos batem com work_calendar (data/tipo/descricao) — não há coluna de
// horas de jornada implementada no backend.
export type CalendarDay = {
  data: string;
  tipo: string;
  descricao: string | null;
};

export type TaskSearchResult = {
  id: string;
  name: string;
  projectId: string;
  projectName: string;
};

export type TaskWithAssignees = Task & { assigneeIds: string[]; assigneeNomes: string[] };

export type KpiSnapshot = {
  data: string;
  valor: number;
};

export type AutomationRule = {
  id: string;
  nome: string;
  projectId: string | null;
  triggerEvent: string;
  acoes: unknown[];
  ativo: boolean;
  criadoEm: string;
  atualizadoEm: string;
};

export type CustomFieldValue = {
  definitionId: string;
  chave: string;
  label: string;
  tipo: string;
  valor: string | number | boolean | null;
};

export type ResourceCalendar = {
  userId: string;
  semana: string;
  horasCapacidade: number;
};

export type CostReportRow = {
  taskId: string;
  taskNome: string;
  estimativaHoras: number;
  horasRealizadas: number;
  tarifaHora: number;
  custoRealizado: number;
};

export type PortfolioRow = {
  id: string;
  nome: string;
  status: string;
  rag: 'verde' | 'amarelo' | 'vermelho';
  progressoPct: number;
  gerente: string | null;
  portfolioNome: string | null;
  programaNome: string | null;
  dataFimPlanejada: string | null;
  dataFimProjetada: string | null;
  spi: number | null;
  cpi: number | null;
  bac: number | null;
  ac: number | null;
  raidOpen: { critico: number; alto: number; medio: number; baixo: number };
  proximoMarco: { nome: string; dataFim: string } | null;
};

export type TimesheetGridActivity = {
  projectId: string;
  projectNome: string;
  taskId: string;
  taskNome: string;
  status: string;
  porColuna: (number | null)[];
  total: number;
};
export type TimesheetGridPerson = {
  userId: string;
  userNome: string;
  totalPorColuna: number[];
  sobrecarga: boolean[];
  atividades: TimesheetGridActivity[];
};
export type TimesheetGrid = {
  period: 'week' | 'month' | 'year';
  colunas: string[];
  pessoas: TimesheetGridPerson[];
};

export type TaskAnalytics = {
  totalAtivas: number;
  atrasadas: number;
  percentConcluidas: number;
  diasAtePrazo: number | null;
  porResponsavel: { userId: string; nome: string; porStatus: { status: string; total: number }[] }[];
  porStatus: { status: string; total: number }[];
};

// No Oracle Cloud, apps/web e apps/api ficam sob o mesmo domínio (Caddy
// roteia /api/* pro backend) — path relativo funciona sem configuração.
// No Cloudflare, o frontend (Pages) e o backend (Worker) ficam em domínios
// diferentes (*.pages.dev / *.workers.dev), então a base da API precisa ser
// configurável via env var de build. Sem VITE_API_BASE_URL definida, cai no
// path relativo de sempre — comportamento do Oracle Cloud 100% preservado.
export const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    // headers depois do spread: senão init.headers substituiria o merge e
    // derrubaria o content-type default.
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    let body: { error?: { code: string; message: string; details?: unknown } } = {};
    try {
      body = await res.json();
    } catch {
      // not json
    }
    const code = body.error?.code ?? 'UNKNOWN';
    const message = body.error?.message ?? res.statusText;
    throw new ApiError(code, message, res.status, body.error?.details);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function buildQs(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) q.set(k, v);
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const api = {
  auth: {
    login: (input: LoginInput) =>
      request<LoginResponse>('/auth/login', { method: 'POST', body: JSON.stringify(input) }),
    logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),
    me: () =>
      request<{
        id: string;
        email: string;
        nome: string;
        tenantId: string;
        roles: string[];
        permissions: string[];
      }>('/auth/me'),
    acceptInvite: (token: string, password: string) =>
      request<{ ok: boolean }>('/auth/accept-invite', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      }),
  },
  projects: {
    list: (params: { programId?: string; status?: string } = {}) =>
      request<{ items: Project[]; nextCursor: string | null }>(`/projects${buildQs(params)}`),
    // Rota PMO devolve o project direto (sem envelope { project }).
    get: (id: string) => request<Project>(`/projects/${id}`),
    create: (data: { programId: string; codigo: string; nome: string; cliente?: string; gerenteId?: string; dataInicio?: string; dataFim?: string; billable?: boolean }) =>
      request<Project>('/projects', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: Partial<{ codigo: string; nome: string; cliente: string; gerenteId: string; dataInicio: string; dataFim: string; status: string; billable: boolean }>) =>
      request<Project>(`/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    lockBaseline: (id: string) =>
      request<{ locked: number }>(`/projects/${id}/baseline`, { method: 'POST' }),
    taskAnalytics: (id: string) => request<TaskAnalytics>(`/projects/${id}/task-analytics`),
  },
  users: {
    list: () => request<{ users: { id: string; nome: string; email: string; ativo: boolean; roles: string[]; criadoEm: string }[] }>('/admin/users'),
  },
  // RoleService não tem campo "descricao" (não existe no schema/DB) e o campo
  // de permissões é "permissions" (inglês) — ver apps/api/src/services/role.service.ts.
  roles: {
    list: () => request<{ roles: { id: string; nome: string; permissions: string[]; criadoEm: string }[] }>('/admin/roles'),
    create: (body: { nome: string; permissions: string[] }) =>
      request<{ id: string; nome: string; permissions: string[]; criadoEm: string }>('/admin/roles', { method: 'POST', body: JSON.stringify(body) }),
    update: (roleId: string, permissions: string[]) =>
      request<{ ok: boolean }>(`/admin/roles/${roleId}`, { method: 'PATCH', body: JSON.stringify({ permissions }) }),
    assign: (userId: string, roleId: string) =>
      request<{ ok: boolean }>('/admin/roles/assignments', { method: 'POST', body: JSON.stringify({ userId, roleId }) }),
    unassign: (userId: string, roleId: string) =>
      request<{ ok: boolean }>('/admin/roles/assignments', { method: 'DELETE', body: JSON.stringify({ userId, roleId }) }),
  },
  smtp: {
    get: () => request<SmtpConfig>('/admin/smtp'),
    save: (body: SmtpConfig) =>
      request<{ ok: boolean }>('/admin/smtp', { method: 'PUT', body: JSON.stringify(body) }),
    test: (body: SmtpConfig) =>
      request<{ ok: boolean; error?: string }>('/admin/smtp/test', { method: 'POST', body: JSON.stringify(body) }),
  },
  invites: {
    send: (body: { nome: string; email: string; roleId?: string }) =>
      request<{ userId: string; email: string; expiresAt: string }>('/admin/users/invite', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
  gantt: {
    get: (projectId: string) =>
      request<GanttResponse>(`/projects/${projectId}/gantt`),

    updateTaskDates: (projectId: string, taskId: string, data: { dataInicio: string; dataFim: string }) =>
      request<{ task: unknown }>(`/projects/${projectId}/tasks/${taskId}/dates`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    bulkUpdateDates: (
      projectId: string,
      updates: Array<{ taskId: string; dataInicio: string; dataFim: string }>,
    ) =>
      request<{ updated: number }>(`/projects/${projectId}/tasks/bulk-dates`, {
        method: 'PATCH',
        body: JSON.stringify({ updates }),
      }),

    createDependency: (
      projectId: string,
      data: { predecessorId: string; successorId: string; tipo: string; lagDias: number },
    ) =>
      request<unknown>(`/projects/${projectId}/dependencies`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    deleteDependency: (projectId: string, depId: string) =>
      request<{ ok: boolean }>(`/projects/${projectId}/dependencies/${depId}`, {
        method: 'DELETE',
      }),
  },
  timesheets: {
    // GET /timesheets (sem sufixo) é uma listagem gerencial; o timesheet da
    // semana do próprio usuário (com getOrCreate) vive em /timesheets/me.
    get: (week: string) =>
      request<Timesheet>(`/timesheets/me${buildQs({ week })}`),
    grid: (params: { period: 'week' | 'month' | 'year'; ref: string; scope: 'self' | 'team' }) =>
      request<TimesheetGrid>(
        `/timesheets/grid?period=${params.period}&ref=${encodeURIComponent(params.ref)}&scope=${params.scope}`,
      ),
    manager: async (params: { week?: string; userId?: string; status?: string; projectId?: string } = {}) => {
      const { items } = await request<{ items: Timesheet[] }>(
        `/timesheets${buildQs({ userId: params.userId, status: params.status, from: params.week, to: params.week })}`,
      );
      return { timesheets: items };
    },
    submit: (timesheetId: string) =>
      request<Timesheet>(`/timesheets/${timesheetId}/submit`, { method: 'POST' }),
    approve: (timesheetId: string, body: { decision: 'aprovar' | 'rejeitar'; comentario?: string }) =>
      request<Timesheet>(`/timesheets/${timesheetId}/decide`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    approvals: (timesheetId: string) =>
      request<{ items: TimesheetApproval[] }>(`/timesheets/${timesheetId}/approvals`),
  },
  timeEntries: {
    list: (params: { from?: string; to?: string; taskId?: string } = {}) =>
      request<{ items: TimeEntry[] }>(`/time-entries${buildQs(params)}`),
    create: (body: { taskId: string; data: string; horas: number; descricao?: string }) =>
      request<TimeEntry>('/time-entries', { method: 'POST', body: JSON.stringify(body) }),
    update: (entryId: string, body: Partial<{ taskId: string; data: string; horas: number; descricao: string }>) =>
      request<TimeEntry>(`/time-entries/${entryId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (entryId: string) =>
      request<{ ok: boolean }>(`/time-entries/${entryId}`, { method: 'DELETE' }),
  },
  reports: {
    plannedVsActual: (projectId: string, params: { from?: string; to?: string } = {}) =>
      request<{ rows: PlannedVsActualRow[] }>(`/reports/planned-vs-actual/${projectId}${buildQs(params)}`),
    weekly: (params: { from?: string; to?: string; userId?: string } = {}) =>
      request<{ rows: WeeklyReportRow[] }>(`/reports/weekly${buildQs(params)}`),
    exportPlannedVsActual: (projectId: string, params: { from?: string; to?: string } = {}) =>
      `${BASE}/reports/planned-vs-actual/${projectId}/export${buildQs(params)}`,
    exportWeekly: (params: { from?: string; to?: string; userId?: string } = {}) =>
      `${BASE}/reports/weekly/export${buildQs(params)}`,
    timesheetsExportUrl: (params: { from?: string; to?: string; userId?: string; projectId?: string; billable?: '0' | '1' } = {}) =>
      `${BASE}/reports/timesheets/export${buildQs(params)}`,
    cost: (projectId: string) =>
      request<{ rows: CostReportRow[]; totalHoras: number; totalCusto: number }>(`/reports/cost/${projectId}`),
    evm: (projectId: string) =>
      request<{
        bac: number; pv: number; ev: number; ac: number;
        spi: number | null; cpi: number | null;
        vac: number; eac: number;
        progressoPct: number; taskCount: number;
      }>(`/reports/evm/${projectId}`),
    portfolio: (params: { portfolioId?: string; status?: string; from?: string; to?: string } = {}) =>
      request<{ projects: PortfolioRow[] }>(`/reports/portfolio${buildQs(params)}`),
  },
  // Rotas aninhadas em projects/:projectId/tasks/:taskId/comments — não
  // existe endpoint flat /tasks/:taskId/comments nem /comments/:id.
  comments: {
    list: (projectId: string, taskId: string) =>
      request<{ items: Comment[] }>(`/projects/${projectId}/tasks/${taskId}/comments`),
    create: (projectId: string, taskId: string, body: string) =>
      request<Comment>(`/projects/${projectId}/tasks/${taskId}/comments`, {
        method: 'POST', body: JSON.stringify({ body }),
      }),
    update: (projectId: string, taskId: string, commentId: string, body: string) =>
      request<Comment>(`/projects/${projectId}/tasks/${taskId}/comments/${commentId}`, {
        method: 'PATCH', body: JSON.stringify({ body }),
      }),
    delete: (projectId: string, taskId: string, commentId: string) =>
      request<{ ok: boolean }>(`/projects/${projectId}/tasks/${taskId}/comments/${commentId}`, { method: 'DELETE' }),
  },
  projectComments: {
    list: (projectId: string) =>
      request<{ items: Comment[] }>(`/projects/${projectId}/comments`),
    create: (projectId: string, body: string) =>
      request<Comment>(`/projects/${projectId}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),
    update: (projectId: string, commentId: string, body: string) =>
      request<Comment>(`/projects/${projectId}/comments/${commentId}`, { method: 'PATCH', body: JSON.stringify({ body }) }),
    delete: (projectId: string, commentId: string) =>
      request<{ ok: boolean }>(`/projects/${projectId}/comments/${commentId}`, { method: 'DELETE' }),
  },
  programComments: {
    list: (programId: string) =>
      request<{ items: Comment[] }>(`/programs/${programId}/comments`),
    create: (programId: string, body: string) =>
      request<Comment>(`/programs/${programId}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),
    update: (programId: string, commentId: string, body: string) =>
      request<Comment>(`/programs/${programId}/comments/${commentId}`, { method: 'PATCH', body: JSON.stringify({ body }) }),
    delete: (programId: string, commentId: string) =>
      request<{ ok: boolean }>(`/programs/${programId}/comments/${commentId}`, { method: 'DELETE' }),
  },
  activityComments: {
    list: (projectId: string, activityId: string) =>
      request<{ items: Comment[] }>(`/projects/${projectId}/activities/${activityId}/comments`),
    create: (projectId: string, activityId: string, body: string) =>
      request<Comment>(`/projects/${projectId}/activities/${activityId}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),
    update: (projectId: string, activityId: string, commentId: string, body: string) =>
      request<Comment>(`/projects/${projectId}/activities/${activityId}/comments/${commentId}`, { method: 'PATCH', body: JSON.stringify({ body }) }),
    delete: (projectId: string, activityId: string, commentId: string) =>
      request<{ ok: boolean }>(`/projects/${projectId}/activities/${activityId}/comments/${commentId}`, { method: 'DELETE' }),
  },
  attachments: {
    list: (projectId: string, taskId: string) =>
      request<{ items: Attachment[] }>(`/projects/${projectId}/tasks/${taskId}/attachments`),
    create: (projectId: string, taskId: string, data: { filename: string; mime?: string; sizeBytes?: number; storageKey: string }) =>
      request<Attachment>(`/projects/${projectId}/tasks/${taskId}/attachments`, {
        method: 'POST', body: JSON.stringify(data),
      }),
    delete: (projectId: string, taskId: string, attachmentId: string) =>
      request<{ ok: boolean }>(`/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}`, { method: 'DELETE' }),
  },
  notifications: {
    list: (params: { lidas?: boolean } = {}) =>
      request<{ items: Notification[]; nextCursor: string | null }>(
        `/notifications${params.lidas !== undefined ? `?lidas=${String(params.lidas)}` : ''}`,
      ),
    unreadCount: () => request<{ count: number }>('/notifications/unread-count'),
    markRead: (id: string) => request<{ ok: boolean }>(`/notifications/${id}/read`, { method: 'PATCH' }),
    markAllRead: () => request<{ updated: number }>('/notifications/read-all', { method: 'POST' }),
  },
  search: {
    query: (q: string, type?: string) =>
      request<{ results: SearchResult[] }>(`/search${buildQs({ q, type })}`),
  },
  // GET /audit devolve { logs, total } (não { entries }) — ver
  // apps/api/src/routes/audit.ts / AuditService.query.
  audit: {
    list: (params: { entidade?: string; entidadeId?: string; userId?: string; limit?: string; offset?: string } = {}) =>
      request<{ logs: AuditEntry[]; total: number }>(`/audit${buildQs(params)}`),
  },
  resources: {
    // GET /resources/utilization devolve o array direto (sem envelope) —
    // ver apps/api/src/routes/resources.pmo.ts.
    utilization: (params: { from: string; to: string; projectId?: string }) =>
      request<ResourceUtilization[]>(`/resources/utilization${buildQs(params)}`),
    overload: () =>
      request<{ overloaded: { userId: string; userNome: string; userEmail: string; totalAlocacao: number; taskCount: number }[] }>('/resources/overload'),
    // Capacidade crua por usuário/semana (resource_calendars) — distinto do
    // cache agregado workload_snapshots. Usado pela aba "Capacidade Semanal".
    calendar: (semanas: string[]) =>
      request<{ items: ResourceCalendar[] }>(`/resources/calendar${buildQs({ semanas: semanas.join(',') })}`),
    // Body é { capacidadeHoras }, não { horasCapacidade } — nome do campo no
    // Zod da rota (calendarBody em resources.pmo.ts) é o inverso do nome de
    // campo usado na resposta de GET /resources/calendar (horasCapacidade).
    updateCalendar: (userId: string, semana: string, capacidadeHoras: number) =>
      request<{ ok: boolean }>(`/resources/calendar/${userId}/${semana}`, {
        method: 'PUT',
        body: JSON.stringify({ capacidadeHoras }),
      }),
  },
  kanban: {
    get: (projectId?: string) =>
      request<{ columns: { a_fazer: KanbanCard[]; em_andamento: KanbanCard[]; em_revisao: KanbanCard[]; bloqueada: KanbanCard[]; concluida: KanbanCard[]; cancelada: KanbanCard[] } }>(
        `/kanban${buildQs({ projectId })}`,
      ),
    move: (taskId: string, status: string) =>
      request<{ ok: boolean; taskId: string; status: string }>(`/kanban/${taskId}/move`, {
        method: 'PATCH', body: JSON.stringify({ status }),
      }),
  },
  calendar: {
    get: (year: number, month: number, params: { projectId?: string; userId?: string } = {}) =>
      request<{ year: number; month: number; from: string; to: string; tasks: CalendarTask[]; entries: CalendarEntry[]; calendarDays: CalendarDay[] }>(
        `/calendar${buildQs({ year: String(year), month: String(month), ...params })}`,
      ),
  },
  // Rotas PMO de portfolio/program/project devolvem o objeto direto no corpo
  // (sem envelope { portfolio }/{ program }/{ project }) — ver apps/api/src/routes/*.ts.
  portfolios: {
    list: () => request<{ items: { id: string; nome: string; codigo: string; status: string; descricao: string | null }[]; nextCursor: string | null }>('/portfolios'),
    get: (id: string) => request<{ id: string; nome: string; codigo: string; status: string; descricao: string | null }>(`/portfolios/${id}`),
    create: (body: { codigo: string; nome: string; descricao?: string }) =>
      request<{ id: string }>('/portfolios', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: { nome?: string; codigo?: string; descricao?: string; status?: string }) =>
      request<{ id: string }>(`/portfolios/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => request<{ ok: boolean }>(`/portfolios/${id}`, { method: 'DELETE' }),
  },
  programs: {
    list: (portfolioId?: string) =>
      request<{ items: { id: string; nome: string; codigo: string; portfolioId: string; status: string; descricao: string | null }[]; nextCursor: string | null }>(
        `/programs${portfolioId ? `?portfolioId=${portfolioId}` : ''}`,
      ),
    get: (id: string) => request<{ id: string; nome: string; codigo: string; portfolioId: string; status: string }>(`/programs/${id}`),
    create: (body: { portfolioId: string; codigo: string; nome: string; descricao?: string }) =>
      request<{ id: string }>('/programs', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: { nome?: string; codigo?: string; descricao?: string; status?: string }) =>
      request<{ id: string }>(`/programs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => request<{ ok: boolean }>(`/programs/${id}`, { method: 'DELETE' }),
  },
  wbs: {
    list: (projectId: string) =>
      request<{ items: WbsNode[] }>(`/projects/${projectId}/wbs`),
    create: (projectId: string, body: { codigo: string; nome: string; parentId?: string; ordem?: number }) =>
      request<WbsNode>(`/projects/${projectId}/wbs`, { method: 'POST', body: JSON.stringify(body) }),
    update: (projectId: string, nodeId: string, body: { codigo?: string; nome?: string; ordem?: number }) =>
      request<WbsNode>(`/projects/${projectId}/wbs/${nodeId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (projectId: string, nodeId: string) =>
      request<{ ok: boolean }>(`/projects/${projectId}/wbs/${nodeId}`, { method: 'DELETE' }),
  },
  activities: {
    list: (projectId: string) =>
      request<{ items: Activity[] }>(`/projects/${projectId}/activities`),
    create: (projectId: string, body: { codigo: string; nome: string; wbsNodeId?: string; descricao?: string; ordem?: number }) =>
      request<Activity>(`/projects/${projectId}/activities`, { method: 'POST', body: JSON.stringify(body) }),
    update: (projectId: string, activityId: string, body: { codigo?: string; nome?: string; descricao?: string; ordem?: number }) =>
      request<Activity>(`/projects/${projectId}/activities/${activityId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (projectId: string, activityId: string) =>
      request<{ ok: boolean }>(`/projects/${projectId}/activities/${activityId}`, { method: 'DELETE' }),
  },
  timer: {
    get: () => request<{ timer: { id: string; taskId: string; taskNome: string; iniciadoEm: string } | null }>('/timer'),
    // POST /timer/start e /timer/stop devolvem o objeto direto (sem envelope
    // { timer }/{ entry }) — só GET /timer é envelopado.
    start: (taskId: string) => request<{ id: string; taskId: string; iniciadoEm: string }>('/timer/start', { method: 'POST', body: JSON.stringify({ taskId }) }),
    stop: () => request<TimeEntry>('/timer/stop', { method: 'POST' }),
    cancel: () => request<{ ok: boolean }>('/timer', { method: 'DELETE' }),
  },
  tasks: {
    list: (projectId: string, params: { activityId?: string; status?: string; cursor?: string } = {}) =>
      request<{ items: TaskWithAssignees[]; nextCursor: string | null }>(`/projects/${projectId}/tasks${buildQs(params)}`),
    // As rotas PMO devolvem a task direto no corpo (sem envelope { task }).
    create: (projectId: string, body: { codigo?: string; nome: string; activityId?: string; status?: string; prioridade?: string; dataInicio?: string; dataFim?: string; estimativaHoras?: number }) =>
      request<Task>(`/projects/${projectId}/tasks`, { method: 'POST', body: JSON.stringify(body) }),
    update: (projectId: string, taskId: string, body: Partial<{ nome: string; status: string; prioridade: string; progressoPct: number; dataInicio: string; dataFim: string; estimativaHoras: number; milestone: boolean }>) =>
      request<Task>(`/projects/${projectId}/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (projectId: string, taskId: string) =>
      request<{ ok: boolean }>(`/projects/${projectId}/tasks/${taskId}`, { method: 'DELETE' }),
    getDependencies: (projectId: string, taskId: string) =>
      request<{ predecessors: TaskDependency[]; successors: TaskDependency[] }>(
        `/projects/${projectId}/tasks/${taskId}/dependencies`,
      ),
    search: (q: string, projectId?: string) =>
      request<{ items: TaskSearchResult[] }>(`/tasks/search${buildQs({ q, ...(projectId ? { projectId } : {}) })}`),
    getSuggestion: (projectId: string) =>
      request<{ estimativaHoras: number; diasParaConclusao: number; dataFimSugerida: string; confianca: 'baixa' | 'media' | 'alta'; n: number } | null>(
        `/projects/${projectId}/task-suggestions`,
      ).catch(() => null),
    createDependency: (
      projectId: string,
      data: { predecessorId: string; successorId: string; tipo: string; lagDias: number },
    ) =>
      request<unknown>(`/projects/${projectId}/dependencies`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    listAssignees: (taskId: string) =>
      request<{ assignees: { userId: string; nome: string; email: string; alocacaoPct: number }[] }>(
        `/tasks/${taskId}/assignees`,
      ),
    addAssignee: (taskId: string, body: { userId: string; alocacaoPct?: number }) =>
      request<{ assignees: { userId: string; nome: string; email: string; alocacaoPct: number }[] }>(
        `/tasks/${taskId}/assignees`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    removeAssignee: (taskId: string, userId: string) =>
      request<{ assignees: { userId: string; nome: string; email: string; alocacaoPct: number }[] }>(
        `/tasks/${taskId}/assignees/${userId}`,
        { method: 'DELETE' },
      ),
  },
  myWork: {
    list: () =>
      request<{
        todas: (Task & { projectNome: string })[];
        vencendoSemana: (Task & { projectNome: string })[];
        atrasadas: (Task & { projectNome: string })[];
      }>('/my-work'),
    teamMembers: () =>
      request<{ items: { id: string; nome: string }[] }>('/my-work/team-members'),
    timeline: (userId?: string) =>
      request<{
        comData: { id: string; nome: string; projectId: string; projectNome: string; dataInicio: string; dataFim: string; progressoPct: number; status: string }[];
        semData: { id: string; nome: string; projectId: string; projectNome: string }[];
        dependencies: { predecessorId: string; successorId: string; tipo: string }[];
      }>(userId ? `/my-work/timeline?userId=${userId}` : '/my-work/timeline'),
  },
  raid: {
    list: (params: { projectId?: string; programId?: string; tipo?: string; status?: string } = {}) =>
      request<{ items: RaidItem[]; nextCursor: string | null }>(`/raid${buildQs(params)}`),
    create: (body: { projectId?: string; programId?: string; tipo: string; titulo: string; descricao?: string; probabilidade?: number; impacto?: number; mitigacao?: string }) =>
      request<RaidItem>('/raid', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Record<string, unknown>) =>
      request<RaidItem>(`/raid/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    close: (id: string) => request<RaidItem>(`/raid/${id}/close`, { method: 'POST' }),
    escalate: (id: string) => request<RaidItem>(`/raid/${id}/escalate`, { method: 'POST' }),
    delete: (id: string) => request<void>(`/raid/${id}`, { method: 'DELETE' }),
  },
  navTree: {
    get: () => request<{ nodes: NavTreeNode[] }>('/nav-tree'),
  },
  // GET/POST/PATCH /blueprints devolvem o objeto direto (sem envelope
  // { blueprint }) — ver apps/api/src/routes/blueprints.pmo.ts.
  blueprints: {
    list: () => request<Blueprint[]>('/blueprints'),
    get: (id: string) => request<Blueprint>(`/blueprints/${id}`),
    create: (body: { tipo: string; nome: string; descricao?: string; body: Record<string, unknown> }) =>
      request<Blueprint>('/blueprints', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: { nome?: string; descricao?: string; ativo?: boolean }) =>
      request<Blueprint>(`/blueprints/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    instantiate: (id: string, projectId: string) =>
      request<{ instanceId: string; wbsCount: number; taskCount: number }>(`/blueprints/${id}/instantiate`, {
        method: 'POST', body: JSON.stringify({ projectId }),
      }),
  },
  // GET/POST /request-forms devolvem o array/objeto direto (sem envelope) —
  // ver apps/api/src/routes/intake.pmo.ts. Submissions é a rota flat
  // /request-submissions?formId=, não aninhada sob /request-forms/:id.
  requestForms: {
    list: () => request<RequestForm[]>('/request-forms'),
    get: (id: string) => request<RequestForm>(`/request-forms/${id}`),
    // Variantes anônimas (página pública /intake/:tenantSlug/:slug): o tenant
    // vai no header x-tenant-slug porque não há sessão.
    getPublic: (tenantSlug: string, slug: string) =>
      request<RequestForm>(`/request-forms/${slug}`, { headers: { 'x-tenant-slug': tenantSlug } }),
    submitPublic: (tenantSlug: string, slug: string, body: { payload: Record<string, unknown> }) =>
      request<RequestSubmission>(`/request-forms/${slug}/submit`, {
        method: 'POST',
        headers: { 'x-tenant-slug': tenantSlug },
        body: JSON.stringify(body),
      }),
    create: (body: { slug: string; nome: string; descricao?: string; schema?: Record<string, unknown>; publico?: boolean }) =>
      request<RequestForm>('/request-forms', { method: 'POST', body: JSON.stringify(body) }),
    submissions: (formId: string, params: { status?: string } = {}) =>
      request<{ items: RequestSubmission[]; nextCursor: string | null }>(`/request-submissions${buildQs({ formId, ...params })}`),
    submit: (formId: string, body: { payload: Record<string, unknown> }) =>
      request<RequestSubmission>(`/request-forms/${formId}/submit`, { method: 'POST', body: JSON.stringify(body) }),
    triage: (submissionId: string, body: { status: string; priorityScore?: number }) =>
      request<RequestSubmission>(`/request-submissions/${submissionId}/triage`, { method: 'PATCH', body: JSON.stringify(body) }),
    convert: (submissionId: string, body: { programId: string; codigo: string; nome: string; blueprintId?: string }) =>
      request<RequestSubmission & { projectId: string }>(`/request-submissions/${submissionId}/convert`, { method: 'POST', body: JSON.stringify(body) }),
  },
  // As rotas de approval-workflows/approval-requests devolvem o objeto direto
  // no corpo (sem envelope { workflow }/{ request }) — ver apps/api/src/routes/approval.pmo.ts.
  approvals: {
    listWorkflows: () => request<ApprovalWorkflow[]>('/approval-workflows'),
    createWorkflow: (body: { nome: string; escopo: string; passos?: unknown[] }) =>
      request<ApprovalWorkflow>('/approval-workflows', { method: 'POST', body: JSON.stringify(body) }),
    listRequests: (params: { status?: string; entidade?: string } = {}) =>
      request<{ items: ApprovalRequest[]; nextCursor: string | null }>(`/approval-requests${buildQs(params)}`),
    submitRequest: (body: { workflowId: string; entidade: string; entidadeId: string; payload?: Record<string, unknown> }) =>
      request<ApprovalRequest>('/approval-requests', { method: 'POST', body: JSON.stringify(body) }),
    decide: (requestId: string, body: { decisao: 'aprovado' | 'rejeitado' | 'encaminhado'; comentario?: string }) =>
      request<ApprovalRequest>(`/approval-requests/${requestId}/decide`, { method: 'POST', body: JSON.stringify(body) }),
    updateWorkflow: (id: string, body: { nome?: string; passos?: { nome: string; aprovadorRole: string; ordem: number }[] }) =>
      request<ApprovalWorkflow>(`/approval-workflows/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },
  scorecards: {
    list: (params: { escopo?: string; escopoId?: string } = {}) =>
      request<Scorecard[]>(`/scorecards${buildQs(params)}`),
    create: (body: { nome: string; escopo: string; escopoId?: string; kpis?: unknown[] }) =>
      request<{ scorecard: Scorecard }>('/scorecards', { method: 'POST', body: JSON.stringify(body) }),
    compute: (id: string) =>
      request<{ ok: boolean }>(`/scorecards/${id}/compute`, { method: 'POST' }),
  },
  objectives: {
    list: (params: { escopo?: string; escopoId?: string } = {}) =>
      request<Objective[]>(`/objectives${buildQs(params)}`),
    create: (body: { escopo: string; escopoId: string; nome: string; descricao?: string; periodo?: string }) =>
      request<Objective>('/objectives', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: { nome?: string; descricao?: string; status?: string; periodo?: string }) =>
      request<Objective>(`/objectives/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    keyResults: (objectiveId: string) =>
      request<KeyResult[]>(`/objectives/${objectiveId}/key-results`),
    createKeyResult: (objectiveId: string, body: { descricao: string; baseline?: number; target?: number; unidade?: string }) =>
      request<KeyResult>(`/objectives/${objectiveId}/key-results`, { method: 'POST', body: JSON.stringify(body) }),
    updateKeyResult: (krId: string, body: { atual?: number; target?: number; descricao?: string }) =>
      request<KeyResult>(`/key-results/${krId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },
  automationRules: {
    list: (params: { projectId?: string } = {}) =>
      request<AutomationRule[]>(`/automation-rules${buildQs(params)}`),
    create: (body: { nome: string; projectId?: string; triggerEvent: string; acoes?: unknown[] }) =>
      request<AutomationRule>('/automation-rules', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: { nome?: string; triggerEvent?: string; ativo?: boolean; acoes?: unknown[] }) =>
      request<AutomationRule>(`/automation-rules/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) =>
      request<void>(`/automation-rules/${id}`, { method: 'DELETE' }),
  },
  kpi: {
    history: (kpiKey: string, escopoId: string, days = 30) =>
      request<KpiSnapshot[]>(`/kpi-snapshots${buildQs({ kpiKey, escopoId, days: String(days) })}`),
  },
  customFields: {
    listDefinitions: (params: { escopo?: string } = {}) =>
      request<CustomFieldDefinition[]>(`/custom-fields${buildQs(params)}`),
    createDefinition: (body: { escopo: string; chave: string; label: string; tipo: string; opcoes?: string[]; required?: boolean; ordem?: number }) =>
      request<CustomFieldDefinition>('/custom-fields', { method: 'POST', body: JSON.stringify(body) }),
    updateDefinition: (id: string, body: { label?: string; opcoes?: string[]; required?: boolean; ordem?: number }) =>
      request<CustomFieldDefinition>(`/custom-fields/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteDefinition: (id: string) =>
      request<void>(`/custom-fields/${id}`, { method: 'DELETE' }),
    getValues: (entityType: string, entityId: string) =>
      request<CustomFieldValue[]>(`/custom-fields/values/${entityType}/${entityId}`),
    setValues: (entityType: string, entityId: string, values: { definitionId: string; valor: string | number | boolean | null }[]) =>
      request<{ ok: boolean }>(`/custom-fields/values/${entityType}/${entityId}`, { method: 'PUT', body: JSON.stringify({ values }) }),
  },
  acl: {
    list: (resourceType: string, resourceId: string) =>
      request<{ grants: AclGrant[] }>(`/admin/acl-grants${buildQs({ resourceType, resourceId })}`),
    grant: (body: { resourceType: string; resourceId: string; principalType: 'user' | 'role'; principalId: string; permission: string }) =>
      request<AclGrant>('/admin/acl-grants', { method: 'POST', body: JSON.stringify(body) }),
    revoke: (grantId: string) =>
      request<{ ok: boolean }>(`/admin/acl-grants/${grantId}`, { method: 'DELETE' }),
  },
};

export { ApiError };
